import { DestinationEntrance, ProcessDetail } from "./application-route-helpers";
import type { AppScreen, ScreenContext } from "./application-route-types";
import { lazyScreen } from "./lazy-screen";
import { ADVANCE_REQUESTS, EXPENSE_REQUESTS, TRAVEL_REQUESTS } from "./request-surfaces";

const Advances = lazyScreen(() => import("./advances"), "Advances");
const AssetRequests = lazyScreen(() => import("./asset-request"), "AssetRequests");
const Compensation = lazyScreen(() => import("./compensation"), "Compensation");
const CompensationAwardPlanDetail = lazyScreen(
  () => import("./compensation-awards"),
  "CompensationAwardPlanDetail",
);
const CompensationAwardStatementDetail = lazyScreen(
  () => import("./compensation-awards"),
  "CompensationAwardStatementDetail",
);
const CompensationAwards = lazyScreen(() => import("./compensation-awards"), "CompensationAwards");
const CompensationBonuses = lazyScreen(
  () => import("./compensation-bonuses"),
  "CompensationBonuses",
);
const CompensationBonusPlanDetail = lazyScreen(
  () => import("./compensation-bonuses"),
  "CompensationBonusPlanDetail",
);
const CompensationBonusStatementDetail = lazyScreen(
  () => import("./compensation-bonuses"),
  "CompensationBonusStatementDetail",
);
const CompensationMerit = lazyScreen(() => import("./compensation-merit"), "CompensationMerit");
const CompensationMeritPlanDetail = lazyScreen(
  () => import("./compensation-merit"),
  "CompensationMeritPlanDetail",
);
const ClaimsAgainstYou = lazyScreen(() => import("./equipment"), "ClaimsAgainstYou");
const Equipment = lazyScreen(() => import("./equipment"), "Equipment");
const Expense = lazyScreen(() => import("./expense"), "Expense");
const ExpensePayments = lazyScreen(() => import("./expense-payments"), "ExpensePayments");
const ExpenseReports = lazyScreen(() => import("./expense-reports"), "ExpenseReports");
const ExpenseDepartmentBudgets = lazyScreen(
  () => import("./expense-department-budgets"),
  "ExpenseDepartmentBudgets",
);
const ExpensePolicy = lazyScreen(() => import("./expense-policy"), "ExpensePolicy");
const PayrollInputs = lazyScreen(() => import("./payroll-inputs"), "PayrollInputs");
const LeaveCostReport = lazyScreen(() => import("./leave-cost-report"), "LeaveCostReport");
const RequestsScreen = lazyScreen(() => import("./requests"), "RequestsScreen");
const SalaryBands = lazyScreen(() => import("./salary-bands"), "SalaryBands");
const OfferPackages = lazyScreen(() => import("./offer-packages"), "OfferPackages");
const Time = lazyScreen(() => import("./time"), "Time");
const Travel = lazyScreen(() => import("./travel"), "Travel");
const TravelDetail = lazyScreen(() => import("./travel"), "TravelDetail");

// Normalize the same verified session field once. These route projections do not
// infer permissions; each screen and the API retain their original admission.
const rolesOf = (state: ScreenContext["state"]) => state.riksu.roles ?? [];

export const WORK_SCREENS: readonly AppScreen[] = [
  // `person` is null whenever this principal is nobody's employment record, and the
  // screen says so rather than asking riksu for advances that belong to nobody.
  {
    path: "/expense",
    render: ({ state }) => <DestinationEntrance at="/expense" state={state} />,
  },
  {
    path: "/compensation",
    render: ({ state }) => <DestinationEntrance at="/compensation" state={state} />,
  },
  {
    path: "/compensation/mine",
    render: ({ state }) => (
      <Compensation
        showing="mine"
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
      />
    ),
  },
  {
    path: "/compensation/awards",
    render: ({ state }) => <CompensationAwards roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/awards/statements/:statement_id",
    render: ({ state }) => <CompensationAwardStatementDetail roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/bonuses",
    render: ({ state }) => <CompensationBonuses roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/bonuses/statements/:statement_id",
    render: ({ state }) => <CompensationBonusStatementDetail roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/merit",
    render: ({ state }) => <CompensationMerit roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management",
    render: ({ state }) => <DestinationEntrance at="/compensation/management" state={state} />,
  },
  {
    path: "/compensation/management/records",
    render: ({ state }) => (
      <Compensation
        showing="records"
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
      />
    ),
  },
  {
    path: "/compensation/management/awards",
    render: ({ state }) => <CompensationAwards roles={rolesOf(state)} surface="administration" />,
  },
  {
    path: "/compensation/management/awards/plans/:plan_id",
    render: ({ state }) => <CompensationAwardPlanDetail roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/bonuses",
    render: ({ state }) => <CompensationBonuses roles={rolesOf(state)} surface="administration" />,
  },
  {
    path: "/compensation/management/bonuses/plans/:plan_id",
    render: ({ state }) => <CompensationBonusPlanDetail roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/merit",
    render: ({ state }) => <CompensationMerit roles={rolesOf(state)} surface="administration" />,
  },
  {
    path: "/compensation/management/merit/plans/:plan_id",
    render: ({ state }) => <CompensationMeritPlanDetail roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/bands",
    render: ({ state }) => <SalaryBands roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/offer-packages",
    render: ({ state }) => <OfferPackages roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/payroll-inputs",
    render: ({ state }) => <PayrollInputs roles={rolesOf(state)} />,
  },
  {
    path: "/compensation/management/leave-costs",
    render: ({ state }) => <LeaveCostReport roles={rolesOf(state)} />,
  },
  {
    path: "/expense/claims",
    // The ROLES decide what the claims list is SAID to be, never what it holds:
    // riksu-authz narrows the read on its own, and an expense administrator and a line
    // manager get different answers with neither of them wrong.
    render: ({ state }) => (
      <Expense personId={state.riksu.person?.person_id ?? null} roles={rolesOf(state)} />
    ),
  },
  {
    path: "/expense/payments",
    render: ({ state }) => <ExpensePayments roles={rolesOf(state)} />,
  },
  {
    path: "/expense/reports",
    render: ({ state }) => (
      <ExpenseReports
        roles={rolesOf(state)}
        advanceEnabled={state.riksu.modules.includes("advance")}
      />
    ),
  },
  {
    path: "/expense/policy/department-budgets",
    render: ({ state }) => <ExpenseDepartmentBudgets roles={rolesOf(state)} />,
  },
  {
    path: "/expense/policy",
    render: ({ state }) => <ExpensePolicy roles={rolesOf(state)} />,
  },
  // EXPENSE APPROVALS, WHICH THE PRODUCT OWNER ASKED FOR BY NAME, and its sibling. It was a
  // `<Section>` at the foot of the claims screen with a segmented control in its heading —
  // so the list of other people's money waiting on this reader's decision had no name in the
  // sidebar, no entry in the palette and no crumb of its own. Same pair, same reasoning and
  // same component as `/leave` above.
  {
    path: "/expense/requests",
    render: ({ state }) => (
      <RequestsScreen
        surface={EXPENSE_REQUESTS}
        showing="mine"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/expense/requests/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/expense/approvals",
    render: ({ state }) => (
      <RequestsScreen
        surface={EXPENSE_REQUESTS}
        showing="awaiting"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/expense/approvals/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  // THE AVANS IS ITS OWN MODULE, at its own one-segment address — asked for three times.
  // Company money already in somebody's hands, two approval gates rather than one, and a
  // balance that decides whether a claim turns into money at all. It was a SECTION of
  // `/expense` at `/expense/advances`, which is *two sections under one module* and not the
  // two modules he asked for; `destinations.ts` carries why the address moved with it.
  //
  // The ROLES go in because recording a hand-back takes `finance.admin`, which is narrower
  // than the expense administrators: the screen hides that row action rather than offering
  // one riksu will refuse.
  {
    path: "/advance",
    render: ({ state }) => <DestinationEntrance at="/advance" state={state} />,
  },
  {
    path: "/advance/balances",
    render: ({ state }) => (
      <Advances personId={state.riksu.person?.person_id ?? null} roles={rolesOf(state)} />
    ),
  },
  // ITS OWN PAIR, and the advance chain alone. While the two modules shared
  // `/expense/approvals` an advance waiting on somebody's decision appeared under a heading
  // naming the other module's money — and the sent list was a table whose first column told
  // you which of two things each row was, which is the shape the product owner objected to
  // twice. `requests.tsx` carries the surfaces and what the split costs.
  {
    path: "/advance/requests",
    render: ({ state }) => (
      <RequestsScreen
        surface={ADVANCE_REQUESTS}
        showing="mine"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/advance/requests/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/advance/approvals",
    render: ({ state }) => (
      <RequestsScreen
        surface={ADVANCE_REQUESTS}
        showing="awaiting"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/advance/approvals/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/travel",
    render: ({ state }) => <DestinationEntrance at="/travel" state={state} />,
  },
  {
    path: "/travel/requests",
    render: ({ state }) => <Travel personId={state.riksu.person?.person_id ?? null} />,
  },
  {
    path: "/travel/requests/:travel_request_id",
    render: ({ state }) => <TravelDetail roles={rolesOf(state)} />,
  },
  {
    path: "/travel/sent",
    render: ({ state }) => (
      <RequestsScreen
        surface={TRAVEL_REQUESTS}
        showing="mine"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/travel/sent/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  {
    path: "/travel/approvals",
    render: ({ state }) => (
      <RequestsScreen
        surface={TRAVEL_REQUESTS}
        showing="awaiting"
        personId={state.riksu.person?.person_id ?? null}
      />
    ),
  },
  {
    path: "/travel/approvals/:process_id",
    render: ({ state }) => <ProcessDetail state={state} />,
  },
  // THE PRINCIPAL GOES IN for the reason it goes into `/leave` and `/expense`: every one of
  // the four time routes names an `employment_id`, so the screen has to choose a person
  // before it can ask riksu anything — and the person it should open on is the CALLER.
  // Handed nothing it would have to default to whoever sorts first, which is a UUIDv7 and
  // therefore the earliest-created person in the tenant: the same stranger every time.
  // `null` is ordinary and the screen says so rather than asking about nobody.
  // THE READER'S OWN ZİMMET, and the principal goes in for the reason it goes into `/time`,
  // `/leave` and `/expense`: the screen is about ONE person and that person is the caller.
  // `null` is an ordinary state — a login riksu cannot resolve to an employment record — and
  // the screen says so rather than asking riksu about nobody.
  //
  // Separate from `/assets`, which is the administrator's four screens behind a role. This
  // one is `everyone`, because riksu narrows both of its reads to the caller on its own.
  {
    path: "/assets/mine",
    render: ({ state }) => <DestinationEntrance at="/assets/mine" state={state} />,
  },
  {
    path: "/assets/mine/holdings",
    render: ({ state }) => <Equipment personId={state.riksu.person?.person_id ?? null} />,
  },
  // ASKING FOR EQUIPMENT — the module's one self-service write, and the half of `/assets/mine`
  // that only existed from 2026-08-13. The principal goes in for the reason it goes into
  // the screen above: the list is titled *your* requests and riksu narrows the read to the
  // caller's subtree, so the screen needs to know which of those rows are the reader's.
  //
  // NOT ON `/assets`, which is the administrator's four screens behind a role.
  // `check-parity.mjs` declared the three request routes headless and named this screen as
  // where the worker's half belongs; the approver's queue is `/assets`' and is not built.
  {
    path: "/assets/mine/requests",
    render: ({ state }) => <AssetRequests personId={state.riksu.person?.person_id ?? null} />,
  },
  // WHAT IS CLAIMED AGAINST THE READER — a `Section` on `/assets/mine` until 2026-08-13, so the
  // rail entry said *What you hold* over a screen carrying this as well. It is a different
  // subject with a statute attached (4857 md. 35, TBK md. 407/2), which is exactly why it
  // does not belong under a heading naming something else.
  {
    path: "/assets/mine/claims",
    render: ({ state }) => <ClaimsAgainstYou personId={state.riksu.person?.person_id ?? null} />,
  },
  {
    path: "/time",
    render: ({ state }) => <DestinationEntrance at="/time" state={state} />,
  },
  {
    path: "/time/entries",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        // THE SCREEN IS STILL EVERYBODY'S. The roles decide ONE control on it — whether the
        // contracted week may be moved — and not whether the destination is reachable.
        roles={rolesOf(state)}
        surface="entries"
      />
    ),
  },
  {
    path: "/time/assessment/summary",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="assessment"
      />
    ),
  },
  {
    path: "/time/assessment/shifts",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="shifts"
      />
    ),
  },
  {
    path: "/time/assessment/days",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="days"
      />
    ),
  },
  {
    path: "/time/assessment/holidays",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="holidays"
      />
    ),
  },
  {
    path: "/time/overtime",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="bands"
      />
    ),
  },
  {
    path: "/time/annual-ceiling",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="ceiling"
      />
    ),
  },
  {
    path: "/time/overtime-elections",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="elections"
      />
    ),
  },
  {
    path: "/time/extraordinary-work",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="authorisations"
      />
    ),
  },
  {
    path: "/time/contracted-weeks",
    render: ({ state }) => (
      <Time
        personId={state.riksu.person?.person_id ?? null}
        roles={rolesOf(state)}
        surface="contracted"
      />
    ),
  },
];
